import { useCallback, useEffect, useState } from "react";
import { useSendUserOperation, useWaitForUserOperation } from "@coinbase/cdp-hooks";
import type { EvmAddress, EvmUserOperationNetwork, Hex } from "@coinbase/cdp-core";
import { encodeFunctionData } from "viem";
import { hybridEscrowAbi as escrowAbi } from "@hybrid-escrow/chain";
import { attributionOptions } from "./attribution";
import { sponsorshipOptions } from "./sponsorship";

export type EscrowActionPhase =
  "idle" | "awaiting-signature" | "submitted" | "confirmed" | "failed";

interface SubmittedOp {
  hash: Hex;
  smartAccount: EvmAddress;
  network: EvmUserOperationNetwork;
}

type EscrowFunctionName = "release" | "openDispute" | "claimAfterDeadline";

/**
 * Sends a single, directly-signed escrow contract call (`release`, `openDispute`,
 * `claimAfterDeadline`) from the connected Smart Account, using the committed HybridEscrow ABI
 * from `@hybrid-escrow/chain` (built from `contracts/out/HybridEscrow.sol` — the actual deployed
 * contract, not a hand-reconstructed guess).
 */
export function useEscrowAction() {
  const { sendUserOperation } = useSendUserOperation();
  const [submittedOp, setSubmittedOp] = useState<SubmittedOp | null>(null);
  const [phase, setPhase] = useState<EscrowActionPhase>("idle");
  const [error, setError] = useState<string | null>(null);

  const wait = useWaitForUserOperation({
    userOperationHash: submittedOp?.hash,
    evmSmartAccount: submittedOp?.smartAccount,
    network: submittedOp?.network,
    enabled: submittedOp !== null,
  });

  useEffect(() => {
    if (!submittedOp) return;
    if (wait.status === "success" && wait.data) {
      if (wait.data.status === "complete") setPhase("confirmed");
      else if (wait.data.status === "dropped" || wait.data.status === "failed") {
        setPhase("failed");
        setError("The transaction did not complete onchain. No state was changed — you can retry.");
      }
    } else if (wait.status === "error") {
      setPhase("failed");
      setError(wait.error?.message ?? "Could not confirm the transaction status.");
    }
  }, [wait.status, wait.data, submittedOp]);

  const sendCall = useCallback(
    async (params: {
      smartAccountAddress: EvmAddress;
      network: EvmUserOperationNetwork;
      to: EvmAddress;
      data: Hex;
      value?: bigint;
    }) => {
      setPhase("awaiting-signature");
      setError(null);
      try {
        const result = await sendUserOperation({
          evmSmartAccount: params.smartAccountAddress,
          network: params.network,
          calls: [{ to: params.to, data: params.data, value: params.value ?? 0n }],
          ...attributionOptions(),
          ...sponsorshipOptions(),
        });
        setSubmittedOp({
          hash: result.userOperationHash,
          smartAccount: params.smartAccountAddress,
          network: params.network,
        });
        setPhase("submitted");
        return result.userOperationHash;
      } catch (err) {
        setPhase("failed");
        setError(
          err instanceof Error
            ? err.message
            : "The wallet rejected or failed to submit the transaction.",
        );
        return null;
      }
    },
    [sendUserOperation],
  );

  /** Encodes `functionName(args)` against the committed HybridEscrow ABI and sends it. Use for
   *  `release`/`claimAfterDeadline`, which need nothing beyond the orderId already known
   *  client-side. */
  const submit = useCallback(
    async (params: {
      smartAccountAddress: EvmAddress;
      network: EvmUserOperationNetwork;
      escrowContractAddress: EvmAddress;
      functionName: EscrowFunctionName;
      args: readonly [Hex] | readonly [Hex, Hex];
    }) => {
      const data = encodeFunctionData({
        abi: escrowAbi,
        functionName: params.functionName,
        args: params.args as never,
      });
      return sendCall({
        smartAccountAddress: params.smartAccountAddress,
        network: params.network,
        to: params.escrowContractAddress,
        data,
      });
    },
    [sendCall],
  );

  /** Sends a call whose calldata was already built server-side (e.g. `openDispute`'s
   *  evidenceHash, which must match the hash the API committed to the Dispute record and the
   *  chain worker will validate — never re-derive it client-side). */
  const submitRaw = useCallback(
    async (params: {
      smartAccountAddress: EvmAddress;
      network: EvmUserOperationNetwork;
      to: EvmAddress;
      data: Hex;
      value?: bigint;
    }) => sendCall(params),
    [sendCall],
  );

  const reset = useCallback(() => {
    setSubmittedOp(null);
    setPhase("idle");
    setError(null);
  }, []);

  return {
    phase,
    transactionHash: wait.data?.transactionHash ?? null,
    error,
    submit,
    submitRaw,
    reset,
  };
}
